
import React, { useState, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useDropzone } from 'react-dropzone';
import Icon from './Icon';
import { useLanguage } from '../context/LanguageContext';

interface ImageUploaderProps {
    onFilesChange: (files: File[]) => void;
    disabled?: boolean;
    multiple?: boolean;
}

interface ImageUploaderRef {
    clearFiles: () => void;
}

const ImageUploader = forwardRef<ImageUploaderRef, ImageUploaderProps>(({ onFilesChange, disabled, multiple = true }, ref) => {
    const { t } = useLanguage();
    const [files, setFiles] = useState<File[]>([]);

    useImperativeHandle(ref, () => ({
        clearFiles: () => {
            setFiles([]);
        }
    }));

    const onDrop = useCallback((acceptedFiles: File[]) => {
        let newFiles;
        if (multiple) {
            newFiles = [...files, ...acceptedFiles].slice(0, 5);
        } else {
            newFiles = acceptedFiles.slice(0, 1);
        }
        setFiles(newFiles);
        onFilesChange(newFiles);
    }, [files, onFilesChange, multiple]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': ['.jpeg', '.png', '.jpg', '.webp'] },
        disabled,
        multiple: multiple
    });

    const removeFile = (fileToRemove: File) => {
        const newFiles = files.filter(file => file !== fileToRemove);
        setFiles(newFiles);
        onFilesChange(newFiles);
    };

    const previews = useMemo(() => files.map(file => ({
        ...file,
        preview: URL.createObjectURL(file)
    })), [files]);

    React.useEffect(() => {
        return () => previews.forEach(file => URL.revokeObjectURL(file.preview));
    }, [previews]);

    // Compact Mode when files exist
    if (previews.length > 0) {
        return (
            <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
                {previews.map((file, index) => (
                    <div key={index} className="relative w-16 h-16 shrink-0 rounded-lg overflow-hidden border border-black/10 dark:border-white/10 group">
                        <img src={file.preview} alt="preview" className="w-full h-full object-cover" />
                        <button
                            onClick={() => removeFile(files[index])}
                            className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white"
                        >
                            <Icon name="close" className="w-4 h-4" />
                        </button>
                    </div>
                ))}
                 {multiple && files.length < 5 && (
                    <div {...getRootProps()} className="w-16 h-16 shrink-0 rounded-lg border border-dashed border-slate-300 dark:border-white/20 flex items-center justify-center cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                        <input {...getInputProps()} />
                        <Icon name="plus" className="w-4 h-4 text-slate-400" />
                    </div>
                 )}
            </div>
        );
    }

    return (
        <div
            {...getRootProps()}
            className={`flex flex-col justify-center items-center w-full h-[100px] border border-dashed rounded-xl transition-all duration-300
            ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            ${isDragActive 
                ? 'border-apple-gold bg-apple-gold/5' 
                : 'border-slate-300 dark:border-white/10 hover:border-slate-400 dark:hover:border-white/30 bg-white/50 dark:bg-white/5'}`}
        >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-1">
                <Icon name="upload" className="w-5 h-5 text-slate-400" />
                <p className="text-xs uppercase font-bold tracking-wider text-slate-500 text-center">
                    {isDragActive ? "Drop here" : (multiple ? "Upload Ref Images" : "Upload Image")}
                </p>
            </div>
        </div>
    );
});

export default ImageUploader;
